import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const quickLinks = [
    { label: 'About', href: '#about' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Buying Guide', href: '#buying-guide' },
    { label: 'Safety Guide', href: '#safety-guide' },
    { label: 'FAQ', href: '#faq' },
  ];

  const articleLinks = [
    { label: 'CSSBuy Spreadsheet Ultimate Guide', href: '/articles/cssbuy-spreadsheet-ultimate-guide' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setFormMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d95q5bjjjt5fme3c518g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* parsing failed, use empty object */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || 'Something went wrong.';
        setFormStatus('error');
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-background-900 text-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-file-list-3-line text-xl text-primary-400"></i>
              </div>
              <span className="font-heading text-lg text-background-50 font-semibold">CSSBuy Sheet</span>
            </div>
            <p className="text-sm text-background-400 leading-relaxed mb-5">
              Your trusted source for the latest CSSBuy spreadsheet collections. Updated daily with verified products, QC photos, and trusted seller links.
            </p>
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 rounded-full bg-background-800 hover:bg-primary-600 flex items-center justify-center transition-colors" aria-label="Twitter">
                <i className="ri-twitter-x-line text-sm text-background-200"></i>
              </a>
              <a href="#" className="w-9 h-9 rounded-full bg-background-800 hover:bg-primary-600 flex items-center justify-center transition-colors" aria-label="Reddit">
                <i className="ri-reddit-line text-sm text-background-200"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <a href={link.href} className="text-sm text-background-400 hover:text-background-100 transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">Articles</h4>
            <ul className="space-y-2.5">
              {articleLinks.map((link, i) => (
                <li key={i}>
                  <Link to={link.href} className="text-sm text-background-400 hover:text-background-100 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mt-6 mb-4">Categories</h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 7).map((cat, i) => (
                <li key={i}>
                  <a href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-background-400 hover:text-background-100 transition-colors">
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">Newsletter</h4>
            <p className="text-sm text-background-400 mb-4">Get the latest spreadsheet updates and shopping tips.</p>
            <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-3">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-background-800 border border-background-700 rounded-lg text-background-100 placeholder:text-background-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer"
                >
                  {formStatus === 'loading' ? 'Sending...' : 'Subscribe'}
                </button>
              </div>
              {formStatus === 'success' && (
                <p className="text-xs text-primary-400">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-xs text-red-400">{formMessage}</p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-background-700 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-500">
            &copy; {new Date().getFullYear()} cssspreadsheet.website. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-background-500">
            <a href="#" className="hover:text-background-300 transition-colors">Privacy Policy</a>
            <span className="text-background-700">|</span>
            <a href="#" className="hover:text-background-300 transition-colors">Terms</a>
            <span className="text-background-700">|</span>
            <a href="#" className="hover:text-background-300 transition-colors">Disclaimer</a>
          </div>
        </div>
      </div>
    </footer>
  );
}